import { useLocation } from "react-router-dom";
import { useReactable } from "@reactables/react";
import { RxPolicyVerification } from "./RxPolicyVerification";
import API from "Helpers/API";
import { Form, Field } from "@reactables/react-forms";
import { ExtendedMeta } from "@jauntin/utilities";
import CoverageService from "Helpers/CoverageService";
import PageNotFoundRedirect from "@gbli-events/common/src/Components/PageNotFoundRedirect";
import ScrollToTop from "Helpers/ScrollToTop";
import { ButtonStyles } from "@gbli-events/common/src/Components/Themed/Button";
import styled from "styled-components";
import Alert from "@gbli-events/common/src/Components/Themed/Alert";
import StyledTextInput from "@gbli-events/common/src/Components/FormElements/TextField";
import StyledIntegerInput from "@gbli-events/common/src/Components/FormElements/IntegerNumberInput/StyledIntegerNumberInput";
import { TwoStageButton } from "@jauntin/react-ui";
import PolicyValid from "@gbli-events/common/src/Assets/Images/secure-shield.svg";
import PolicyInvalid from "@gbli-events/common/src/Assets/Images/no-circle.svg";
import ReCaptchaLoader from "@gbli-events/common/src/Components/ReCaptchaLoader";
import {
  GBLI_EMAIL_EVENTS,
  GBLI_PHONE_EVENTS,
  GBLI_PHONE_EVENTS_TITLE,
} from "@gbli-events/common/src/Constants/shared";
import { hasControlTouchError } from "@gbli-events/common/src/Rx/Selectors/hasControlTouchError.selector";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { setResolvingQueryParams } from "Actions/actions";

const StyledTwoStageButton = styled(TwoStageButton)(
  (props) =>
    `${ButtonStyles(props)}
    width: 400px;
  `,
);

const PolicyVerification = () => {
  const dispatch = useDispatch();
  const coverageKey = new URLSearchParams(useLocation().search).get("c");
  const [state, actions] = useReactable(RxPolicyVerification, {
    coverageKey,
    coverageService: new CoverageService(new API()),
  });
  // TODO: Remove this effect when we fully migrate to reactables
  useEffect(() => {
    dispatch(setResolvingQueryParams(false));
  }, [dispatch]);

  if (!state) return;

  const {
    form: { root },
    submission,
  } = state;

  const showErrors = ({ touched, error }: ExtendedMeta) => {
    return Boolean((touched || state.submitTouched) && error);
  };

  if (!coverageKey) return <PageNotFoundRedirect notFoundRedirectUrl="/" />;

  return (() => {
    if (submission.success) {
      const { effectiveDate, status, valid } = submission.data;

      const Icon = valid ? PolicyValid : PolicyInvalid;

      return (
        <ScrollToTop>
          <div className="policy-verification">
            <div className="container">
              <div className="page-heading mt-0">
                <div className="text-center mb-5">
                  <img src={Icon} alt="Policy Authenticator" />
                </div>
                <h2>Policy Verification Results</h2>
              </div>
              <div className="mb-5 pt-2 text-center">
                This page confirms the existence and basic eligibility details
                of the policy associated with the scanned QR code and the
                information you provided.
                <br />
                Below is the verified policy information:
              </div>
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="policy-verification__info-line">
                    <div className="d-flex">
                      <div className="policy-verification__info-label-flex">
                        <div className="font-weight-bold">Policy status:</div>
                      </div>
                      <div className="policy-verification__info-value-flex">
                        <div className="text-capitalize">{status}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="policy-verification__info-line">
                    <div className="d-flex">
                      <div className="policy-verification__info-label-flex">
                        <div className="font-weight-bold">
                          {`${valid ? "Policy Effective Date" : "Original Policy Effective Date"}:`}
                        </div>
                      </div>
                      <div className="policy-verification__info-value-flex">
                        <div className="text-capitalize">{effectiveDate}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="policy-verification__info-block text-center mt-5">
                <div className="mb-4">
                  <i className="far fa-exclamation-circle policy-verification__alert-icon" />
                  If the policy details appear to be incorrect, outdated, or if
                  this page does not match the document you received, please let
                  us know immediately.
                </div>
                <div className="mb-4">
                  Report an issue:{" "}
                  <a
                    href={`mailto:${GBLI_EMAIL_EVENTS}`}
                    className="policy-verification__info-link"
                  >
                    {GBLI_EMAIL_EVENTS}
                  </a>
                  <br />
                  Call us at:{" "}
                  <a
                    href={`tel:${GBLI_PHONE_EVENTS_TITLE}`}
                    className="policy-verification__info-link"
                  >
                    {GBLI_PHONE_EVENTS}
                  </a>{" "}
                  option 2
                </div>
                <div>
                  Your help ensures the security and trust of all parties
                  involved.
                </div>
              </div>
            </div>
          </div>
        </ScrollToTop>
      );
    }

    return (
      <div className="policy-verification">
        <div className="container">
          <div className="page-heading">
            <h2>Policy Authenticator</h2>
          </div>
          <div className="mb-5 pt-2">
            Please fill out all fields below to verify policy. Make sure the
            information entered exactly matches the details in the policy
            documents.
          </div>
          <div className="policy-verification__form-container">
            <Form rxForm={[state.form, actions.form]}>
              <div className="form-row">
                <div className="col-12 col-sm-6 mb-3">
                  <Field
                    name="policyNumber"
                    component={StyledTextInput}
                    label="Policy Number"
                    labelClassName="label--required"
                    placeholder="ABC0000000"
                    showErrors={showErrors}
                    errorMessages={{
                      length10: "Invalid, must be 10 characters",
                    }}
                  />
                </div>
                <div className="col-12 col-sm-6 mb-3">
                  <Field
                    name="insuredName"
                    component={StyledTextInput}
                    label="Insured Last Name/Company Name"
                    labelClassName="label--required"
                    placeholder="Insured Last Name/Company Name"
                    showErrors={showErrors}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-12 col-sm-6 mb-3">
                  <Field
                    name="venueZip"
                    component={StyledTextInput}
                    inputMode="numeric"
                    label="Venue's Zipcode"
                    labelClassName="label--required"
                    placeholder="Venue's Zipcode"
                    showErrors={showErrors}
                  />
                </div>
                <div className="col-12 col-sm-6 mb-3">
                  <Field
                    name="dailyAttendance"
                    component={StyledIntegerInput}
                    inputMode="numeric"
                    label="Daily Attendees Quantity"
                    labelClassName="label--required"
                    placeholder="Daily Attendees Quantity"
                    showErrors={showErrors}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-12 col-sm-6 mb-3">
                  <Field
                    name="insuredZip"
                    component={StyledTextInput}
                    inputMode="numeric"
                    label="Insured's Zipcode"
                    labelClassName="label--required"
                    placeholder="Insured's Zipcode"
                    showErrors={showErrors}
                  />
                </div>
              </div>
            </Form>
          </div>
          {state.submission.error && (
            <Alert
              variant="danger"
              className="mb--2rem flex-wrap align-items-start"
            >
              <i className="far fa-exclamation-circle alert__icon" />
              <div className="alert__text">
                The information you provided does not match our records. To
                validate the authenticity of the insurance policy, please
                contact us at{" "}
                <a
                  href={`mailto:${GBLI_EMAIL_EVENTS}`}
                  className="text-underline"
                >
                  {GBLI_EMAIL_EVENTS}
                </a>{" "}
                or{" "}
                <a href={`tel:${GBLI_PHONE_EVENTS}`} className="text-underline">
                  {GBLI_PHONE_EVENTS}
                </a>{" "}
                option 2.
              </div>
            </Alert>
          )}
          <div className="d-flex justify-content-center">
            <StyledTwoStageButton
              actionEnabled={root.valid}
              action={() => {
                actions.submission.send({
                  formValue: state.form.root.value,
                });
                actions.form.markControlAsUntouched([]);
              }}
              spinning={submission.loading}
              disabled={
                submission.loading ||
                hasControlTouchError(state.form) ||
                (state.submitTouched && !state.form.root.touched)
              }
              touch={actions.submitTouched.toggleOn}
              className="btn"
              text="Verify Now"
            />
          </div>
          <div className="grecaptcha-text">
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-underline"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer"
              className="text-underline"
            >
              Terms of Service
            </a>{" "}
            apply.
          </div>
        </div>
      </div>
    );
  })();
};

const PolicyVerificationPage = () => (
  <ReCaptchaLoader>
    <PolicyVerification />
  </ReCaptchaLoader>
);

export default PolicyVerificationPage;
